/**
 * School years, and the terms inside the one you are looking at.
 *
 * The year you are editing used to live in a query parameter and be chosen
 * from a dropdown, which hid the thing you most need to see here: how many
 * years exist and which one is current. They are a short list, so this shows
 * the list and lets you pick from it.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import { Button } from '~/components/ui/button';
import { CreateSchoolYearDialog } from '~/pages/Dashboard/CreateSchoolYearDialog';
import TermsSection from '~/pages/SchoolYearView/TermsSection';
import { parseLocalDate } from '~/utils/paymentUtils';
import { isCurrentYear } from '~/utils/schoolYearState';
import { SettingsBlock, SettingsEmpty } from './SettingsPanel';
import { useMakeSchoolYearCurrent } from './useMakeSchoolYearCurrent';
import type { AppLayoutContext } from '~/types';

type SchoolYearSummary = AppLayoutContext['school']['schoolYears'][number];

interface SchoolYearsEditorProps {
  schoolId: string;
  schoolYears: readonly SchoolYearSummary[];
  /** The year the rest of the app is working in, used as the opening choice. */
  selectedSchoolYearId?: string;
  onSchoolYearCreated: (schoolYear?: { id: string; slug: string }) => void;
  /** Another year became the current one, so every year's state has changed. */
  onCurrentYearChanged: () => void;
}

// A year's dates are calendar days, so they're read where the office is, not
// as midnight in UTC (which west of it is the evening before).
function formatDate(value: unknown): string {
  if (!value) return '—';
  const date = parseLocalDate(String(value));
  if (!date || Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function SchoolYearsEditor({
  schoolId,
  schoolYears,
  selectedSchoolYearId,
  onSchoolYearCreated,
  onCurrentYearChanged,
}: SchoolYearsEditorProps) {
  const [createOpen, setCreateOpen] = useState(false);
  const [openYearId, setOpenYearId] = useState<string | null>(null);
  const [makeCurrentErrors, setMakeCurrentErrors] = useState<string[]>([]);
  // The year waiting on the office to confirm it should become the current one.
  // It stays set while the dialog closes so its name doesn't vanish mid-fade.
  const [confirmingYear, setConfirmingYear] = useState<SchoolYearSummary | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const [makeSchoolYearCurrent, { loading: makingCurrent }] = useMakeSchoolYearCurrent();

  const handleMakeCurrent = async (schoolYearId: string) => {
    setMakeCurrentErrors([]);
    try {
      const result = await makeSchoolYearCurrent({ variables: { schoolYearId } });
      const userErrors = result.data?.makeSchoolYearCurrent?.userErrors ?? [];
      if (userErrors.length > 0) {
        setMakeCurrentErrors(userErrors.map((error) => error.message));
        return;
      }
      onCurrentYearChanged();
    } catch (error) {
      setMakeCurrentErrors([error instanceof Error ? error.message : 'The year was not made current. Please try again.']);
    }
  };

  // A link from the schedule or a course names the year it needs terms for.
  const linkedYearSlug = searchParams.get('year');

  const selected =
    schoolYears.find((year) => year.id === openYearId)
    ?? schoolYears.find((year) => year.slug === linkedYearSlug)
    ?? schoolYears.find((year) => year.id === selectedSchoolYearId)
    ?? schoolYears[schoolYears.length - 1]
    ?? null;

  // A year created from here becomes the one you are looking at.
  useEffect(() => {
    if (openYearId && !schoolYears.some((year) => year.id === openYearId)) setOpenYearId(null);
  }, [openYearId, schoolYears]);

  return (
    <>
      <SettingsBlock
        title="School years"
        blurb="Every enrollment, course, and gradebook belongs to one of these."
        action={
          <Button onClick={() => setCreateOpen(true)} variant="outline" className="gap-2">
            <Plus className="h-4 w-4" />
            Add school year
          </Button>
        }
      >
        {schoolYears.length === 0 ? (
          <SettingsEmpty
            action={
              <Button onClick={() => setCreateOpen(true)} className="gap-2">
                <Plus className="h-4 w-4" />
                Add school year
              </Button>
            }
          >
            No school years yet. Add one before setting up terms, courses, or enrollment.
          </SettingsEmpty>
        ) : (
          <ul className="divide-y rounded-lg border">
            {schoolYears.map((year) => {
              const isOpen = selected?.id === year.id;
              return (
                <li key={year.id} className={`flex items-center gap-2 pr-3 ${isOpen ? 'bg-muted' : ''}`}>
                  <button
                    type="button"
                    onClick={() => setOpenYearId(year.id)}
                    aria-current={isOpen ? 'true' : undefined}
                    className={`flex min-w-0 flex-1 flex-wrap items-center justify-between gap-x-4 gap-y-1 px-4 py-3 text-left transition-colors ${
                      isOpen ? '' : 'hover:bg-muted/50'
                    }`}
                  >
                    <span className="font-medium">{year.name}</span>
                    <span className="text-sm tabular-nums text-muted-foreground">
                      {formatDate(year.startDate)} – {formatDate(year.endDate)}
                    </span>
                  </button>
                  {isCurrentYear(year) ? (
                    <span className="w-36 shrink-0 text-center text-sm text-muted-foreground">Current year</span>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-36 shrink-0"
                      disabled={makingCurrent}
                      onClick={() => {
                        setConfirmingYear(year);
                        setConfirmOpen(true);
                      }}
                    >
                      Make current year
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {makeCurrentErrors.length > 0 && (
          <div role="alert" className="mt-3 space-y-1 text-sm text-destructive">
            {makeCurrentErrors.map((message) => (
              <p key={message}>{message}</p>
            ))}
          </div>
        )}
      </SettingsBlock>

      {selected && (
        <SettingsBlock
          title={`Terms in ${selected.name}`}
          blurb="Terms are what a course meets in and what a gradebook is reported against."
        >
          <TermsSection key={selected.id} schoolYearId={selected.id} />
        </SettingsBlock>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Make {confirmingYear?.name} the current year?</AlertDialogTitle>
            <AlertDialogDescription>
              Years that start before it become past and the rest become planned. Anyone opening the app for the
              first time on a device starts in this year; everyone else stays in the year they last had open until
              they change it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (confirmingYear) handleMakeCurrent(confirmingYear.id); }}>
              Make current
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CreateSchoolYearDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        schoolId={schoolId}
        onSuccess={(schoolYear) => {
          if (schoolYear) setOpenYearId(schoolYear.id);
          onSchoolYearCreated(schoolYear);
        }}
      />
    </>
  );
}
